
// ** PRISM **
// These are overridden search results to remove any mention of statistics.
// When we decide on revealing statistics, we can get rid of this.

// This file is part of InvenioRDM
// Copyright (C) 2022-2024 CERN.
//
// Invenio RDM is free software; you can redistribute it and/or modify it
// under the terms of the MIT License; see LICENSE file for more details.

import { i18next } from "@translations/invenio_app_rdm/i18next";
import _get from "lodash/get";
import _truncate from "lodash/truncate";
import PropTypes from "prop-types";
import React from "react";
import { http } from "react-invenio-forms";
import { Button, Dropdown, Item, Label, Icon } from "semantic-ui-react";

import { DisplayPartOfCommunities } from "/js/invenio_app_rdm/components/DisplayPartOfCommunities";
import { SearchItemCreators } from "/js/invenio_app_rdm/utils";


// For global search and within community search
// https://github.com/inveniosoftware/invenio-app-rdm/blob/master/invenio_app_rdm/theme/assets/semantic-ui/js/invenio_app_rdm/components/RecordsResultsListItem.js
function ItemOfRecordSearch({
  id,
  result,
  key,
  accessStatusId,
  accessStatus,
  accessStatusIcon,
  createdDate,
  creators,
  descriptionStripped,
  publicationDate,
  resourceType,
  subjects,
  title,
  version,
  versions,
  allVersionsVisible,
  numOtherVersions,
}) {

  versions = versions || {index: 1};
  subjects = subjects || [];
  const publishingInformation = _get(result, "ui.publishing_information.journal", "");
  const viewLink = `/records/${result.id}`;

  return (
    <Item key={key ?? result.id}>
      <Item.Content>
        {/* FIXME: Uncomment to enable themed banner */}
        {/* <DisplayVerifiedCommunity communities={result.parent?.communities} /> */}
        <Item.Extra className="labels-actions">
          <Label horizontal size="small" className="primary">
            {publicationDate} ({version})
          </Label>
          <Label horizontal size="small" className="neutral">
            {resourceType}
          </Label>
          <Label
            horizontal
            size="small"
            className={`access-status ${accessStatusId}`}
          >
            {accessStatusIcon && <Icon name={accessStatusIcon} />}
            {accessStatus}
          </Label>
        </Item.Extra>
        <Item.Header as="h2">
          <a href={viewLink}>{title}</a>
        </Item.Header>
        <Item className="creatibutors">
          <SearchItemCreators creators={creators} othersLink={viewLink} />
        </Item>
        <Item.Description>
          {_truncate(descriptionStripped, { length: 350 })}
        </Item.Description>
        <Item.Extra>
          {subjects.map((subject) => (
            <Label key={subject.title_l10n} size="tiny">
              {subject.title_l10n}
            </Label>
          ))}

          <div className="flex justify-space-between align-items-end">
            <small>
              <DisplayPartOfCommunities communities={result.parent?.communities} />
              <p>
                {createdDate && (
                  <>
                    {i18next.t("Uploaded on {{uploadDate}}", {
                      uploadDate: createdDate,
                    })}
                  </>
                )}
                {createdDate && publishingInformation && " | "}

                {publishingInformation && (
                  <>
                    {i18next.t("Published in: {{publishInfo}}", {
                      publishInfo: publishingInformation,
                    })}
                  </>
                )}
              </p>

              {!allVersionsVisible && versions.index > 1 && (
                <p>
                  <b>
                    {i18next.t("{{count}} more versions exist for this record", {
                      count: numOtherVersions,
                    })}
                  </b>
                </p>
              )}
            </small>

            {/**
             * ** PRISM **
             * Here we have removed the CompactStats component to hide the
             * stats. When it is time to reintroduce, we will stop overridding
             * this component altogether.
            */}
          </div>
        </Item.Extra>
      </Item.Content>
    </Item>
  );
}


export const ItemOfGlobalRecordSearch = ItemOfRecordSearch;
export const ItemOfCommunityRecordSearch = ItemOfRecordSearch;


const statuses = {
  in_review: { color: "warning", title: i18next.t("In review") },
  declined: { color: "negative", title: i18next.t("Declined") },
  expired: { color: "expired", title: i18next.t("Expired") },
  draft_with_review: { color: "neutral", title: i18next.t("Draft") },
  draft: { color: "neutral", title: i18next.t("Draft") },
  new_version_draft: { color: "neutral", title: i18next.t("New version draft") },
};


// Taken from https://github.com/inveniosoftware/invenio-app-rdm/blob/master/invenio_app_rdm/theme/assets/semantic-ui/js/invenio_app_rdm/user_dashboard/uploads_items/ComputerTabletUploadsItem.js
function ItemOfComputerTabletDashboardRecordSearch({
  result,
  editRecord,
  statuses,
  access,
  uiMetadata,
}) {
  const { accessStatusId, accessStatus, accessStatusIcon } = access;
  const {
    descriptionStripped,
    title,
    creators,
    subjects,
    publicationDate,
    resourceType,
    createdDate,
    version,
    isPublished,
    viewLink,
    publishingInformation,
  } = uiMetadata;

  const icon = isPublished ? (
    <Icon name="check" className="positive" />
  ) : (
    <Icon name="upload" className="negative" />
  );
  /** PRISM - Removed stats */

  return (
    <Item key={result.id} className="deposits-list-item computer tablet only flex">
      <div className="status-icon mr-10">
        <Item.Content verticalAlign="top">
          <Item.Extra>{icon}</Item.Extra>
        </Item.Content>
      </div>
      <Item.Content>
        {/* FIXME: Uncomment to enable themed banner */}
        {/* <DisplayVerifiedCommunity communities={result.parent?.communities} /> */}
        <Item.Extra className="labels-actions">
          {result.status in statuses && result.status !== "published" && (
            <Label horizontal size="small" className={statuses[result.status].color}>
              {statuses[result.status].title}
            </Label>
          )}
          <Label horizontal size="small" className="primary">
            {publicationDate} ({version})
          </Label>
          <Label horizontal size="small" className="neutral">
            {resourceType}
          </Label>
          <Label horizontal size="small" className={`access-status ${accessStatusId}`}>
            <i className={`icon ${accessStatusIcon}`} />
            {accessStatus}
          </Label>
          <Button
            compact
            size="small"
            floated="right"
            onClick={() => editRecord()}
            labelPosition="left"
            icon="edit"
            content={i18next.t("Edit")}
            />
          {isPublished && (
            <Button
            compact
            size="small"
            floated="right"
            href={viewLink}
              labelPosition="left"
              icon="eye"
              content={i18next.t("View")}
              />
            )}
        </Item.Extra>
        <Item.Header as="h2">
          <a href={viewLink} className="truncate-lines-2">
            {title}
          </a>
        </Item.Header>
        <Item.Meta>
          <div className="creatibutors">
            <SearchItemCreators creators={creators} />
          </div>
        </Item.Meta>
        <Item.Description>
          {_truncate(descriptionStripped, {
            length: 350,
          })}
        </Item.Description>
        <Item.Extra>
          {subjects.map((subject) => (
            <Label key={subject.title_l10n} size="tiny">
              {subject.title_l10n}
            </Label>
          ))}

          <div className="flex justify-space-between align-items-end">
            <small>
              <DisplayPartOfCommunities communities={result.parent?.communities} />

              {createdDate ? (
                <>
                  {i18next.t("Uploaded on {{uploadDate}}", { uploadDate: createdDate })}
                </>
              ) : (
                i18next.t("No creation date found.")
              )}
              {publishingInformation && (
                <span>
                  {" "}
                  |{" "}
                  {i18next.t("Published in: {{publishInfo}}", {
                    publishInfo: publishingInformation,
                  })}
                </span>
              )}
            </small>
            {/** PRISM - Removed CompactStats */}
          </div>
        </Item.Extra>
      </Item.Content>
    </Item>
  );
}


function ItemOfMobileDashboardRecordSearch({
  result,
  editRecord,
  statuses,
  access,
  uiMetadata,
}) {
  const { accessStatusId, accessStatus, accessStatusIcon } = access;
  const {
    descriptionStripped,
    title,
    creators,
    subjects,
    publicationDate,
    resourceType,
    createdDate,
    version,
    isPublished,
    viewLink,
    publishingInformation,
  } = uiMetadata;

  const icon = isPublished ? (
    <Icon name="check" className="positive mr-10" />
  ) : (
    <Icon name="upload" className="negative mr-10" />
  );
  /** PRISM - Removed stats */

  return (
    <Item key={result.id} className="deposits-list-item mobile only flex">
      <Item.Content className="centered">
        {/* FIXME: Uncomment to enable themed banner */}
        {/* <DisplayVerifiedCommunity communities={result.parent?.communities} /> */}
        <Item.Extra className="labels-actions">
          {result.status in statuses && result.status !== "published" && (
            <Label horizontal size="small" className={statuses[result.status].color}>
              {statuses[result.status].title}
            </Label>
          )}
          <Label horizontal size="small" className="primary">
            {publicationDate} ({version})
          </Label>
          <Label horizontal size="small" className="neutral">
            {resourceType}
          </Label>
          <Label horizontal size="small" className={`access-status ${accessStatusId}`}>
            <i className={`icon ${accessStatusIcon}`} />
            {accessStatus}
          </Label>
        </Item.Extra>
        <Item.Header as="h2">
          <a href={viewLink} className="truncate-lines-2">
            {icon}
            {title}
          </a>
        </Item.Header>
        <Item.Meta>
          <div className="creatibutors">
            <SearchItemCreators creators={creators} />
          </div>
        </Item.Meta>
        <Item.Description>
          {_truncate(descriptionStripped, {
            length: 100,
          })}
        </Item.Description>
        <Item.Extra>
          <Item.Extra>
            {subjects.map((subject) => (
              <Label key={subject.title_l10n} size="tiny">
                {subject.title_l10n}
              </Label>
            ))}
            <div>
              <small>
                <DisplayPartOfCommunities communities={result.parent?.communities} />
                {createdDate ? (
                  <>
                    {i18next.t("Uploaded on {{uploadDate}}", {
                      uploadDate: createdDate,
                    })}
                  </>
                ) : (
                  i18next.t("No creation date found.")
                )}
              </small>
              <small>
                {publishingInformation && (
                  <span>
                    {i18next.t("Published in: {{publishedIn}}", {
                      publishedIn: publishingInformation,
                    })}
                  </span>
                )}
              </small>
              {/** PRISM - Removed CompactStats */}
            </div>
          </Item.Extra>
        </Item.Extra>
        <Item.Extra>
          <Dropdown button text={i18next.t("Actions")} labeled className="icon">
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => editRecord()}
                labelPosition="left"
                icon="edit"
                content={i18next.t("Edit")}
              />

              {isPublished && (
                <Dropdown.Item
                  labelPosition="left"
                  href={viewLink}
                  icon="eye"
                  content={i18next.t("View")}
                />
              )}
            </Dropdown.Menu>
          </Dropdown>
        </Item.Extra>
      </Item.Content>
    </Item>
  );
}


// Taken from https://github.com/inveniosoftware/invenio-app-rdm/blob/master/invenio_app_rdm/theme/assets/semantic-ui/js/invenio_app_rdm/user_dashboard/uploads.js
export function ItemOfDashboardRecordSearch({ result }) {
  const editRecord = () => {
    http
    .post(
      `/api/records/${result.id}/draft`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/vnd.inveniordm.v1+json",
          },
        }
      )
      .then(() => {
        window.location = `/uploads/${result.id}`;
      })
      .catch((error) => {
        console.error(error.response.data);
      });
  };

  const isPublished = result.is_published;
  const access = {
    accessStatusId: _get(result, "ui.access_status.id", i18next.t("open")),
    accessStatus: _get(result, "ui.access_status.title_l10n", i18next.t("Open")),
    accessStatusIcon: _get(result, "ui.access_status.icon", i18next.t("unlock")),
  };
  const uiMetadata = {
    descriptionStripped: _get(
      result,
      "ui.description_stripped",
      i18next.t("No description")
    ),
    title: _get(result, "metadata.title", i18next.t("No title")),
    creators: _get(result, "ui.creators.creators", []).slice(0, 3),
    subjects: _get(result, "ui.subjects", []),
    publicationDate: _get(
      result,
      "ui.publication_date_l10n_long",
      i18next.t("No publication date found.")
    ),
    resourceType: _get(
      result,
      "ui.resource_type.title_l10n",
      i18next.t("No resource type")
    ),
    createdDate: result.ui?.created_date_l10n_long,
    version: result.ui?.version ?? "",
    isPublished: isPublished,
    viewLink: isPublished ? `/records/${result.id}` : `/uploads/${result.id}`,
    publishingInformation: _get(result, "ui.publishing_information.journal", ""),
  };

  return (
    <>
      {/** PRISM - Use above Components */}
      <ItemOfComputerTabletDashboardRecordSearch
        result={result}
        editRecord={editRecord}
        statuses={statuses}
        access={access}
        uiMetadata={uiMetadata}
      />
      <ItemOfMobileDashboardRecordSearch
        result={result}
        editRecord={editRecord}
        statuses={statuses}
        access={access}
        uiMetadata={uiMetadata}
      />
    </>
  );
};


ItemOfDashboardRecordSearch.propTypes = {
  result: PropTypes.object.isRequired,
};
